import { createContext, useContext, useMemo, type JSX, type ReactNode } from "react";
import { nearestCountryLevelId, type CountryNode } from "@ckgo2/shared";
import type { AdminDestinationListItem } from "@ckgo2/shared";
import { useAdminDestinationList } from "@/hooks/useAdminLists";

/**
 * Země okna výběru fotek z editované destinace (vize podnety-tymu-2026-10, F7.11; řez 05,
 * Kontrakt bod 3). Editor destinace, inspirace a zájezdu obalí formulář providerem s ID
 * své destinace (část K3); okno výběru (`MediaPicker`, část K2) si přes `useMediaPickerScope`
 * přečte zemi a předvyplní jí filtr Země a zemi dávky v záložce Nahrát.
 *
 * Země = nejbližší destinace zemské úrovně (`nearestCountryLevelId`, totéž pravidlo jako
 * země fotky v API) nad stromem ze sdíleného štíhlého seznamu destinací. Bez provideru,
 * před načtením seznamu, u neznámé destinace nebo bez zemské úrovně je země `null` (okno
 * pak filtr země nepředvyplní).
 */

type ScopeRow = Pick<AdminDestinationListItem, "id" | "parentId" | "showAsCountry">;

const MediaPickerScopeContext = createContext<string | null>(null);

export function MediaPickerScopeProvider({
  destinationId,
  children,
}: {
  destinationId: string | null;
  children: ReactNode;
}): JSX.Element {
  return (
    <MediaPickerScopeContext.Provider value={destinationId}>
      {children}
    </MediaPickerScopeContext.Provider>
  );
}

/** Čistá: země zemské úrovně pro destinaci; nenačtený seznam nebo bez zemské úrovně → null. */
export function pickerScopeCountryId(
  destinationId: string | null,
  rows: readonly ScopeRow[] | undefined,
): string | null {
  if (destinationId === null || !rows) return null;
  // Řádky seznamu mají `parentId`/`showAsCountry` volitelné; chybějící = země bez rodiče.
  const byId = new Map<string, CountryNode>(
    rows.map((r) => [
      r.id,
      { id: r.id, parentId: r.parentId ?? null, showAsCountry: r.showAsCountry ?? null },
    ]),
  );
  return nearestCountryLevelId(destinationId, byId);
}

export function useMediaPickerScope(): { countryId: string | null } {
  const destinationId = useContext(MediaPickerScopeContext);
  // Bez destinace (bez provideru, nová destinace) se seznam kvůli zemi nenačítá.
  const { data } = useAdminDestinationList({ enabled: destinationId !== null });
  const rows = data?.data;
  return useMemo(
    () => ({ countryId: pickerScopeCountryId(destinationId, rows) }),
    [destinationId, rows],
  );
}
